import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, Upload, X, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";

export default function NewExpenseDialog({ open, onOpenChange, onCreated }) {
  const fileInputRef = useRef(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("Miscellaneous");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);
  const [categories, setCategories] = useState(["Travel", "Gas", "Miscellaneous", "Lodging", "Food"]);
  const [descriptions, setDescriptions] = useState([]);

  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const [c, d] = await Promise.all([api.get("/categories"), api.get("/descriptions")]);
          setCategories(c.data.categories || []);
          setDescriptions(d.data.descriptions || []);
        } catch { /* ignore */ }
      })();
    } else {
      setDescription("");
      setAmount("");
      setExpenseDate(new Date().toISOString().slice(0, 10));
      setNotes("");
      setCategory("Miscellaneous");
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setReceiptId(null);
      setScanning(false);
      setSaving(false);
      setAiConfidence(null);
    }
    // eslint-disable-next-line
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    // Preview
    const url = URL.createObjectURL(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(url);

    setScanning(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/expenses/scan-receipt", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { receipt_id, amount: aiAmount, description: aiDesc, category: aiCategory, confidence, ai_error } = res.data;
      setReceiptId(receipt_id);
      if (aiAmount != null && !Number.isNaN(Number(aiAmount))) {
        setAmount(String(Number(aiAmount).toFixed(2)));
      }
      if (aiDesc && !description) {
        setDescription(aiDesc);
      }
      if (aiCategory) {
        setCategory(aiCategory);
      }
      setAiConfidence(confidence || "medium");
      if (ai_error) {
        toast.warning("Receipt saved, but AI couldn't read the amount. Please enter it manually.");
      } else if (aiAmount == null) {
        toast.warning("Couldn't confidently read the amount — please double-check.");
      } else {
        toast.success("Receipt scanned");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to scan receipt");
    } finally {
      setScanning(false);
    }
  };

  const removeImage = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setReceiptId(null);
    setAiConfidence(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!description.trim()) return toast.error("Add a description");
    if (Number.isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    if (!expenseDate) return toast.error("Pick a date");
    setSaving(true);
    try {
      await api.post("/expenses", {
        description: description.trim(),
        amount: amt,
        expense_date: new Date(expenseDate).toISOString(),
        receipt_id: receiptId,
        notes: notes.trim(),
        category,
      });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-y-auto max-h-[92dvh] w-[calc(100vw-1.5rem)] sm:w-full" data-testid="new-expense-dialog">
        <div className="grid md:grid-cols-2">
          {/* Left: image */}
          <div className="bg-secondary/60 p-6 md:p-8 md:min-h-[320px] flex flex-col">
            <DialogHeader className="text-left mb-4">
              <DialogTitle className="font-display text-xl font-semibold tracking-tight">Receipt</DialogTitle>
              <DialogDescription className="text-xs">Snap a photo or upload — AI reads the total automatically.</DialogDescription>
            </DialogHeader>

            {previewUrl ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative flex-1 rounded-lg overflow-hidden bg-background border border-border/60">
                <img src={previewUrl} alt="Receipt preview" className="w-full h-full object-contain max-h-[220px] md:max-h-[420px]" data-testid="receipt-preview-img" />
                <button
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-background/90 hover:bg-background border border-border rounded-full p-1.5 shadow-sm"
                  aria-label="Remove image"
                  data-testid="remove-image-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                {scanning && (
                  <div className="absolute inset-0 bg-background/70 backdrop-blur flex items-center justify-center gap-2 text-primary" data-testid="scanning-overlay">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Reading receipt…</span>
                  </div>
                )}
              </motion.div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 rounded-lg border-2 border-dashed border-border/70 bg-background/60 flex flex-col items-center justify-center gap-3 text-muted-foreground hover:border-primary hover:text-primary transition-colors cursor-pointer min-h-[140px] md:min-h-[240px]"
                data-testid="upload-dropzone"
              >
                <ImageIcon className="w-8 h-8" />
                <div className="text-center text-sm">
                  <div className="font-medium">Tap to add a receipt</div>
                  <div className="text-xs opacity-80 mt-1">JPG, PNG · up to 10MB</div>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="receipt-file-input"
            />

            <div className="flex gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => fileInputRef.current?.click()}
                data-testid="take-photo-btn"
              >
                <Camera className="w-4 h-4 mr-2" /> Take photo
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="flex-1 rounded-full"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.removeAttribute("capture");
                    fileInputRef.current.click();
                    fileInputRef.current.setAttribute("capture", "environment");
                  }
                }}
                data-testid="upload-photo-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload
              </Button>
            </div>
          </div>

          {/* Right: form */}
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-4">
            <DialogHeader className="text-left mb-2">
              <DialogTitle className="font-display text-xl font-semibold tracking-tight">Expense details</DialogTitle>
              <DialogDescription className="text-xs">AI fills the amount and description. Edit anything before saving.</DialogDescription>
            </DialogHeader>

            {aiConfidence && (
              <div className="flex items-center gap-2 text-xs text-primary bg-accent/40 border border-border/60 rounded-md px-3 py-2" data-testid="ai-confidence-banner">
                <Sparkles className="w-3.5 h-3.5" />
                <span>AI extracted (confidence: {aiConfidence}). Please verify.</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="amount">Amount (JMD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">J$</span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9 tabular-nums text-lg font-medium"
                  placeholder="0.00"
                  data-testid="amount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                required
                list="description-suggestions"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Type or pick — e.g., Gas, Client dinner"
                data-testid="description-input"
                autoComplete="off"
              />
              <datalist id="description-suggestions" data-testid="description-suggestions">
                {descriptions.map((d) => (
                  <option key={d.description} value={d.description} />
                ))}
              </datalist>
              {descriptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="description-chips">
                  {descriptions.slice(0, 5).map((d) => (
                    <button
                      type="button"
                      key={d.description}
                      onClick={() => setDescription(d.description)}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-border/70 bg-muted/50 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                      data-testid={`desc-chip-${d.description.replace(/\s+/g, "-")}`}
                    >
                      {d.description}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category" data-testid="category-select-trigger">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expense-date">Date</Label>
              <Input
                id="expense-date"
                type="date"
                required
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                data-testid="expense-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Project, attendees, etc."
                data-testid="notes-input"
              />
            </div>

            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="cancel-expense-btn">Cancel</Button>
              <Button type="submit" disabled={saving || scanning} className="rounded-full" data-testid="save-expense-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save expense"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
